import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { BurgerLogoTrigger } from '@apps/admin/components/BurgerLogoTrigger/BurgerLogoTrigger';
import { ADMIN_SHELL_BRAND_GAP, ADMIN_SHELL_BURGER_LOGO, ADMIN_SHELL_TITLE_FONT_WEIGHT, ADMIN_SHELL_TITLE_INSET_X } from './AdminShell.constants';

type AdminShellBrandProps = Readonly<{
  /** Unique DOM id for the particle logo — distinct per mount point. @see BurgerLogoTrigger */
  logoId: string;
  /** Forwarded to the burger menu so the drawer opens against the right event. */
  eventHandle?: string;
  /** Plain product / section name, rendered in the shell title style. Ignored when `titleSlot`
   *  is passed. */
  title?: string;
  /** Replaces the plain title with an interactive one — e.g. a `SectionSwitcherDropdown`. The
   *  slot owns its own typography; `SectionSwitcherDropdown` already matches the shell title. */
  titleSlot?: React.ReactNode;
  hideHamburger?: boolean;
  hideLogo?: boolean;
  /** `true` (default) keeps the logo at every breakpoint, as the website editor's left panel does.
   *  `false` hides it below `md`, where `AdminHeaderBar` needs the room for its actions. */
  logoAlwaysVisible?: boolean;
}>;

/**
 * The left cluster of the admin shell header: hamburger + Joy logo (the shared
 * `BurgerLogoTrigger`) followed by the product title, at the sizes in `AdminShell.constants`.
 * It lays out only the cluster — the host decides the row's height, padding and what sits to its
 * right. Each part is optional: with both `hideHamburger` and `hideLogo` the menu trigger is not
 * rendered at all (no empty, still-clickable zone), and with neither `title` nor `titleSlot`
 * there is no title element.
 */
export const AdminShellBrand: React.FC<AdminShellBrandProps> = ({ logoId, eventHandle, title, titleSlot, hideHamburger = false, hideLogo = false, logoAlwaysVisible = true }) => (
  <Flex
    alignItems="center"
    gap={ADMIN_SHELL_BRAND_GAP}
    minWidth={0}
  >
    {(!hideHamburger || !hideLogo) && (
      <BurgerLogoTrigger
        logoId={logoId}
        eventHandle={eventHandle}
        hideHamburger={hideHamburger}
        hideLogo={hideLogo}
        logoAlwaysVisible={logoAlwaysVisible}
        columnGap={ADMIN_SHELL_BRAND_GAP}
        {...ADMIN_SHELL_BURGER_LOGO}
      />
    )}
    {/* Same typography as `SectionSwitcherDropdown`'s trigger, so a plain title and a dropdown
        title are indistinguishable at rest — change both together. */}
    {titleSlot ??
      (title !== undefined && (
        <TextV2
          typographyVariant="hed2"
          fontWeight={ADMIN_SHELL_TITLE_FONT_WEIGHT}
          color="mono14"
          paddingLeft={ADMIN_SHELL_TITLE_INSET_X}
        >
          {title}
        </TextV2>
      ))}
  </Flex>
);

AdminShellBrand.displayName = 'AdminShellBrand';
